/**
 * Cheap, offline ETA fallback.
 *
 * Straight-line (great-circle) distance divided by an assumed average speed.
 * It deliberately knows nothing about roads, turns or traffic, so it always
 * *under*-estimates travel time in a real street network — it exists so the
 * tracking UI has a number to show before (or instead of) a routing API
 * answer. Platforms that have a routing provider should prefer that provider's
 * duration and use this only as the fallback.
 *
 * Pure and dependency-free on purpose: usable from web, native and tests.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Mean Earth radius (IUGG), metres. */
const EARTH_RADIUS_METERS = 6_371_008.8;

/**
 * Default assumed speed, metres per second (~40 km/h ≈ 25 mph) — a typical
 * door-to-door urban average once junctions and lights are included.
 */
export const DEFAULT_AVG_SPEED_MPS = 11;

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Great-circle distance between two coordinates, in metres.
 */
export function haversineMeters(from: LatLng, to: LatLng): number {
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const fromLat = toRadians(from.lat);
  const toLat = toRadians(to.lat);

  const a =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(fromLat) * Math.cos(toLat);

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Estimated seconds to travel from `from` to `to`, rounded to whole seconds.
 *
 * `avgSpeedMps` must be a finite, positive speed; a live GPS `speed` reading
 * must NOT be passed straight through, because a stopped vehicle reports 0 and
 * an ETA of "infinity" is not a thing any screen can render. Clamp it at the
 * call site (or leave the default) instead.
 *
 * @throws RangeError if `avgSpeedMps` is not finite and > 0.
 */
export function estimateEtaSeconds(
  from: LatLng,
  to: LatLng,
  avgSpeedMps: number = DEFAULT_AVG_SPEED_MPS,
): number {
  if (!Number.isFinite(avgSpeedMps) || avgSpeedMps <= 0) {
    throw new RangeError(`estimateEtaSeconds: avgSpeedMps must be a positive number (got ${avgSpeedMps})`);
  }
  return Math.round(haversineMeters(from, to) / avgSpeedMps);
}
